import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "react-image-crop/dist/ReactCrop.css";
import GlobalStyles from "./styles/GlobalStyles";
import Employees from "./pages/Employees";
import Users from "./pages/Users";
import Settings from "./pages/Settings";
import PageNotFound from "./pages/PageNotFound";
import AppLayout from "./interface/AppLayout";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { UserProvider } from "./contexts/UserContext";
import { DarkModeProvider } from "./contexts/DarkModeContext";
import LunchMenu from "./pages/LunchMenu";
import TelegramDecoder from "./pages/TelegramDecoder";
import TestingOnly from "./pages/TestingOnly";
import Home from "./modules/home/Home";

import ProtectedRoute from "./interface/ProtectedRoute";
import GdprProtectedRoute from "./interface/GdprProtectedRoute";
import ActiveFaults from "./pages/ActiveFaults";
import React, { Suspense, lazy, useState, useEffect } from "react";
import MachineInfo from "./pages/MachineInfo";
import AssetOperationData from "./pages/AssetOperationData";
import LunchMenuEdit from "./modules/lunchMenu/LunchMenuEdit";
import ThisIsTheEnd from "./pages/ThisIsTheEnd";
import PartMatrixDEMO from "./pages/PartMatrixDEMO";
import Yggdrasil from "./pages/Yggdrasil";
import DashboardCustomerUnsecured from "./pages/DashboardCustomerUnsecured";
import IntroFade from "./interface/InftroFade";



const Maintenance = lazy(() => import("./pages/Maintenance"));
const MachineFaults = lazy(() => import("./pages/MachineFaults"));
const ShiftPlan = lazy(() => import("./pages/ShiftPlan"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
    },
  },
});

function App() {
  console.log("App rendered");
  // const [isMobile, setIsMobile] = useState(null); // Initialize as null to show loading state
  // const [isLoading, setIsLoading] = useState(true);

  // Detect if the user is on a mobile device
  // useEffect(() => {
  //   const userAgent = navigator.userAgent || window.opera;

  //   if (/android/i.test(userAgent) || /iPhone|iPad|iPod/.test(userAgent)) {
  //     setIsMobile(true);
  //   } else {
  //     setIsMobile(false);
  //   }
  //   setIsLoading(false); // Stop loading after detection
  // }, []); // Run only once when the component mounts

  // If still detecting the device, show a loading indicator or nothing
  // if (isLoading) {
  //   return <div>Loading...</div>; // You can customize the loading screen
  // }

  return (
    <DarkModeProvider>
      <UserProvider>
        <QueryClientProvider client={queryClient}>
          <GlobalStyles />
          <BrowserRouter>
            <Routes>
              {/* Make exception for Swagger API to avoid interception  */}
              <Route
                path="wiMuleAPI/swagger/*"
                element={
                  <Navigate
                    replace
                    to="https://vinterbro.wioss.no/wiMuleAPI/swagger/index.html"
                  />
                }
              />
              {/* Define the AppLayout for Desktop Routes */}
              
                <Route element={<AppLayout />}>
                  <Route element={<ProtectedRoute />}>
                    <Route path="employees" element={<GdprProtectedRoute />}>
                      <Route index element={<Employees />} />
                    </Route>
                    <Route path="activefaults" element={<ActiveFaults />} />
                    <Route
                      path="maintenance"
                      element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <Maintenance />
                        </Suspense>
                      }
                    />
                    <Route path="users" element={<Users />} />
                    <Route
                      path="shiftplan"
                      element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <ShiftPlan />
                        </Suspense>
                      }
                    />
                    <Route path="settings" element={<Settings />} />
                    <Route path="mi" element={<MachineInfo />} />
                    <Route path="test" element={<TestingOnly />} />
                    <Route path="assetop" element={<AssetOperationData />} />
                  </Route>
                  <Route path="lunchmenu" element={<LunchMenu />} />
                  <Route path="lunchmenuz" element={<LunchMenuEdit />} />
                  <Route path="yggdrasil" element={<Yggdrasil />} />
                  <Route path="matrix" element={<PartMatrixDEMO />} />
                  <Route
                    path="dashboard"
                    element={
                      <IntroFade>
                        <DashboardCustomerUnsecured />
                      </IntroFade>
                    }
                  />
                  <Route path="theend" element={<ThisIsTheEnd />} />
                  <Route
                    path="machinefaults"
                    element={
                      <Suspense fallback={<div>Loading...</div>}>
                        <MachineFaults />
                      </Suspense>
                    }
                  />

                  <Route path="machineinfo" element={<MachineInfo />} />
                  <Route path="telegramdecoder" element={<TelegramDecoder />} />
                </Route>            
              
              {/* Define the MAppLayout for Mobile Routes */}
              {/* {isMobile && (
                
                <Route element={<MAppLayout />}>
                  
                  <Route path="lunchMenu" element={<MLunchMenu />} />
                  <Route
                    path="mobile-machineFaults"
                    element={<MMachineFaults />}
                  />
                  <Route element={<ProtectedRoute />}>
                    <Route path="mi" element={<MachineInfo />} />
                    <Route path="activefaults" element={<ActiveFaults />} />
                    <Route
                      path="maintenance"
                      element={
                        <Suspense fallback={<div>Loading...</div>}>
                          <Maintenance />
                        </Suspense>
                      }
                    />
                  </Route>
                  <Route
                    path="shiftplan"
                    element={
                      <Suspense fallback={<div>Loading...</div>}>
                        <ShiftPlan />
                      </Suspense>
                    }
                  />
                </Route>
              )} */}

              {/* Redirect to mobile or desktop home based on device */}
              <Route
                path="/"
                element={
                  <Navigate replace to={"/home"} />
                }
              />
              <Route path="home" element={<Home />} />
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </BrowserRouter>
          <Toaster
            position="top-center"
            gutter={12}
            containerStyle={{ margin: "8px" }}
            toastOptions={{
              success: { duration: 3000 },
              error: { duration: 5000 },
              position: "bottom-center",
              style: {
                fontSize: "16px",
                maxWidth: "500px",
                padding: "16px 24px",
                backgroundColor: "var(--color-grey-0)",
                color: "var(--color-grey-700)",
              },
            }}
          />
        </QueryClientProvider>
      </UserProvider>
    </DarkModeProvider>
  );
}

export default App;
